/** @jsx jsx */
import {
  Box,
  Button,
  Flex,
  FormLabel,
  FullScreenModal,
  FullScreenModalContainer,
  FullScreenModalHeader,
  FullScreenModalHeading,
  MultiSelect,
  SearchIcon,
  SelectGroup,
  SingleSelect,
  Stack,
  Status,
  TextInput,
} from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { IURLSortConfig } from "@/core/url";
import { ICodelist } from "@/data/codelists";
import { IApplicationURLFilter } from "@/data/job-applications";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useOnChangeFilterHandler } from "@/infrastructure/Filter";
import { ApplicationCandidatesFilter } from "@/views/job-applications/list/filters/ApplicationCandidatesFilter";
import { ApplicationContactFilter } from "@/views/job-applications/list/filters/ApplicationContactFilter";

interface SortOption extends IURLSortConfig {
  label: string;
  value: string;
  group: SelectGroup;
}

interface ApplicationFiltersProps {
  filtersValue: IApplicationURLFilter;
  sortValue?: IURLSortConfig[];
  onFiltersUpdate: (value: any, cb?: () => void) => void;
  onSortUpdate: (value: any, cb?: () => void) => void;
  onFilterClear: () => void;
  hideClearFilters?: boolean;
  applicationCount?: number;
}

export const ApplicationFilters: React.FC<ApplicationFiltersProps> = ({
  filtersValue,
  onFiltersUpdate,
  onSortUpdate,
  sortValue,
  onFilterClear,
}) => {
  const User = useContext(UserContext);
  const filterHandler = useOnChangeFilterHandler;
  const companySearchParams = useMemo(() => ({ companyId: User.contextCompany?.id || 0 }), [User.contextCompany?.id]);
  const tCodelist = useCodelistTranslation();
  const breakpoint = useBreakpointIndex();
  const { t } = useTranslation(["session", "public"]);

  const isManager = User.hasCompanyManagerPrivileges();

  const statusOptions: ICodelist[] = [
    { code: "DRAFT", parameter: "" },
    { code: "SUBMITTED", parameter: "" },
    { code: "PRESENTED_TO_CLIENT", parameter: "" },
    { code: "INTERVIEW", parameter: "" },
    { code: "ACCEPTED", parameter: "" },
    { code: "REJECTED", parameter: "" },
    { code: "WITHDRAWN", parameter: "" },
    { code: "EXPIRED", parameter: "" },
  ];

  const createdGroup: SelectGroup = {
    key: "c",
    label: t("JOB_APPLICATIONS.LIST.SORT.BY"),
    ariaLabel: "No label group",
  };

  const updatedGroup: SelectGroup = {
    key: "no-label",
    ariaLabel: "No label group",
  };

  const sortOptions: SortOption[] = [
    {
      label: "CREATED_FROM_NEWEST",
      sortBy: "createdDate",
      order: "DESC",
      value: "1",
      group: createdGroup,
    },
    {
      label: "CREATED_FROM_OLDEST",
      sortBy: "createdDate",
      order: "ASC",
      value: "2",
      group: createdGroup,
    },
    {
      label: "UPDATED_FROM_NEWEST",
      sortBy: "modifiedDate",
      order: "DESC",
      value: "3",
      group: updatedGroup,
    },
    {
      label: "UPDATED_FROM_OLDEST",
      sortBy: "modifiedDate",
      order: "ASC",
      value: "4",
      group: updatedGroup,
    },
  ];

  const selectedSortOption = sortOptions.find(
    so => so.sortBy === sortValue?.[0]?.sortBy && so.order === sortValue?.[0]?.order,
  );

  const handleSortChange = useCallback(
    (v?: SortOption) => {
      onSortUpdate(
        v
          ? [
              {
                sortBy: v.sortBy,
                order: v.order,
              },
            ]
          : undefined,
      );
    },
    [onSortUpdate],
  );

  const modalState = useDialogState({ animated: true });

  const handleViewResultsButtonClick = useCallback(() => {
    modalState.hide();
  }, [modalState]);

  const additionalFiltersCount =
    (!filtersValue.candidateIds?.length ? 0 : 1) +
    (filtersValue.contactPersonProfileIds?.length ?? 0) +
    (filtersValue?.status?.length ? 1 : 0);

  return (
    <React.Fragment>
      <Flex gap={["xs", null, null, "sm"]} flexWrap="wrap" flexGrow={1}>
        <Box sx={{ width: ["100%", null, "50%", 240] }}>
          <TextInput
            icon={<SearchIcon />}
            size="md"
            placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.SEARCH_APPLICATIONS")}
            value={filtersValue.query}
            onChange={filterHandler("query", onFiltersUpdate)}
          />
        </Box>
        <Box>
          <SingleSelect
            options={sortOptions}
            value={selectedSortOption}
            identifierSelector={c => c.value}
            labelSelector={c => t(`JOB_APPLICATIONS.LIST.SORT.${c.label}`)}
            onChange={handleSortChange}
            clearable={false}
          />
        </Box>
        {breakpoint >= 3 ? (
          <React.Fragment>
            {User.contextCompany?.id && isManager && (
              <React.Fragment>
                <Box sx={{ maxWidth: 200 }}>
                  <ApplicationCandidatesFilter
                    name="candidateIds"
                    placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.CANDIDATE")}
                    profileIds={filtersValue.candidateIds}
                    onChange={onFiltersUpdate}
                    searchParams={companySearchParams}
                  />
                </Box>
                <Box sx={{ maxWidth: 180 }}>
                  <ApplicationContactFilter
                    name="contactPersonProfileIds"
                    placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.APPLICATION_CONTACT")}
                    profileIds={filtersValue.contactPersonProfileIds}
                    onChange={onFiltersUpdate}
                    searchParams={companySearchParams}
                  />
                </Box>
              </React.Fragment>
            )}
            <Box sx={{ maxWidth: 180 }}>
              <MultiSelect
                placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.SELECT_STATUS")}
                options={statusOptions}
                value={filtersValue.status}
                identifierSelector={(c: ICodelist) => c.code}
                labelSelector={(c: ICodelist) => tCodelist("job-request-application-statuses", c.code)}
                onChange={filterHandler("status", onFiltersUpdate)}
              />
            </Box>

            {additionalFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={onFilterClear}>
                {t("JOB_APPLICATIONS.LIST.FILTERS.CLEAR_ALL")}
              </Button>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Box>
              <Button
                as={DialogDisclosure}
                variant="secondary"
                size="sm"
                iconLeft={additionalFiltersCount > 0 ? <Status>{additionalFiltersCount}</Status> : undefined}
                {...modalState}>
                {t("JOB_APPLICATIONS.LIST.FILTERS.SECTION_HEADER")}
              </Button>
            </Box>

            <FullScreenModal dialogState={modalState}>
              <FullScreenModalContainer>
                <FullScreenModalHeader
                  sx={{ "> [data-dialog-header-main='true']": { alignItems: "center" } }}
                  onCloseClick={modalState.hide}
                  closeButtonAriaLabel={t("JOB_APPLICATIONS.LIST.FILTERS.CLOSE")}>
                  <Flex gap="xs">
                    <FullScreenModalHeading size="h-sm">
                      {t("JOB_APPLICATIONS.LIST.FILTERS.SECTION_HEADER")}
                    </FullScreenModalHeading>

                    {additionalFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={onFilterClear}>
                        {t("JOB_APPLICATIONS.LIST.FILTERS.CLEAR_ALL")}
                      </Button>
                    )}
                  </Flex>
                </FullScreenModalHeader>

                <Box p="sm">
                  <Flex gap="lg" flexDirection="column">
                    <Flex gap="sm" flexDirection="column">
                      {User.contextCompany?.id && isManager && (
                        <React.Fragment>
                          <Flex gap="xxs" flexDirection="column">
                            <FormLabel label={t("JOB_APPLICATIONS.LIST.FILTERS.CANDIDATE")} />
                            <ApplicationCandidatesFilter
                              name="candidateIds"
                              placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.CANDIDATE")}
                              profileIds={filtersValue.candidateIds}
                              onChange={onFiltersUpdate}
                              searchParams={companySearchParams}
                            />
                          </Flex>
                          <Flex gap="xxs" flexDirection="column">
                            <FormLabel label={t("JOB_APPLICATIONS.LIST.FILTERS.APPLICATION_CONTACT")} />
                            <ApplicationContactFilter
                              name="contactPersonProfileIds"
                              placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.APPLICATION_CONTACT")}
                              profileIds={filtersValue.contactPersonProfileIds}
                              onChange={onFiltersUpdate}
                              searchParams={companySearchParams}
                            />
                          </Flex>
                        </React.Fragment>
                      )}
                      <Flex gap="xxs" flexDirection="column">
                        <FormLabel label={t("JOB_APPLICATIONS.LIST.FILTERS.SELECT_STATUS")} />
                        <MultiSelect
                          placeholder={t("JOB_APPLICATIONS.LIST.FILTERS.SELECT_STATUS")}
                          options={statusOptions}
                          value={filtersValue.status}
                          identifierSelector={(c: ICodelist) => c.code}
                          labelSelector={(c: ICodelist) => tCodelist("job-request-application-statuses", c.code)}
                          onChange={filterHandler("status", onFiltersUpdate)}
                        />
                      </Flex>
                    </Flex>

                    <Stack gap="xs">
                      <Button onClick={handleViewResultsButtonClick}>
                        {t("JOB_APPLICATIONS.LIST.FILTERS.VIEW_RESULTS")}
                      </Button>
                    </Stack>
                  </Flex>
                </Box>
              </FullScreenModalContainer>
            </FullScreenModal>
          </React.Fragment>
        )}
      </Flex>
    </React.Fragment>
  );
};
